/**
 * lib/hrImport.ts
 * ------------------------------------------------------------------
 * Shared CSV-import helpers — the reusable scaffold behind every HR
 * bulk importer (staff opening balances, Stanbic loans, Bomaid
 * premiums, …). Pure functions only, so they're unit-tested and the
 * importer components stay thin.
 *
 *   parseCsv      → header row + data rows (quote-aware)
 *   makeColFinder → match a column by any of several candidate names
 *   numOr / boolOf→ tolerant cell coercion
 *   downloadCsv   → push a template/file to the browser
 * ------------------------------------------------------------------
 */

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
  error?: string;
}

/** Normalise a header for fuzzy matching: lower-case, alphanumerics only. */
export function normHeader(h: string): string {
  return h.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Split one CSV line, honouring simple double-quoted fields. */
export function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') inQ = false;
      else cur += c;
    } else if (c === '"') {
      inQ = true;
    } else if (c === ',') {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur.trim());
  return out;
}

/** Parse CSV text into headers + data rows. */
export function parseCsv(src: string): ParsedCsv {
  const lines = src.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) {
    return { headers: [], rows: [], error: 'The file needs a header row and at least one data row.' };
  }
  const headers = splitCsvLine(lines[0]);
  const rows = lines.slice(1).map(splitCsvLine);
  return { headers, rows };
}

/**
 * Returns a finder bound to a header row. Call it with candidate names
 * (e.g. ['employeeno','empno','staffno']) and it returns the matching
 * column index, or -1. Matches on exact normalised equality first, then
 * "contains".
 */
export function makeColFinder(headers: string[]): (names: string[]) => number {
  const norm = headers.map(normHeader);
  return (names) => {
    const cands = names.map(normHeader);
    let idx = norm.findIndex((h) => cands.some((c) => h === c));
    if (idx < 0) idx = norm.findIndex((h) => cands.some((c) => h.includes(c)));
    return idx;
  };
}

/** Cell value at an index (safe). */
export function cell(row: string[], i: number): string {
  return i >= 0 && i < row.length ? row[i] : '';
}

/** Coerce a cell to a number; blank/invalid → fallback. Strips commas/spaces. */
export function numOr(v: string | undefined, dflt = 0): number {
  if (v == null || v.trim() === '') return dflt;
  const n = Number(v.replace(/[\s,]/g, ''));
  return Number.isFinite(n) ? n : dflt;
}

/** True when a cell is present and non-blank. */
export function hasValue(v: string | undefined): boolean {
  return v != null && v.trim() !== '';
}

/** Coerce a cell to a boolean (yes/y/true/1/eligible → true). */
export function boolOf(v: string | undefined, dflt = false): boolean {
  if (v == null || v.trim() === '') return dflt;
  return /^(y|yes|true|1|eligible|active)$/i.test(v.trim());
}

/** Push a CSV string to the browser as a download. */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}
